import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Star, Gift, CreditCard, Check, Users, Settings, Crown, Shield } from 'lucide-react';
import { getBackendUrl, getApiUrl } from '../utils/config';

const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

// Subscription Plans Component
const SubscriptionPlans = ({ user, onBack }) => {
  const [plans, setPlans] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription-plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    try {
      const response = await axios.post(`${API}/subscriptions/redeem`, {
        code: promoCode.trim()
      });
      toast.success(response.data.message);
      setPromoCode('');
      // Refresh user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem promo code');
    }
  };

  const handleSubscribe = async (plan) => {
    // Check if user is already on this plan
    const currentPlan = user?.subscription?.plan_name || 'Free';
    
    if (plan.name === 'Free') {
      if (currentPlan === 'Free') {
        toast.info('You are already on the Free plan');
        return;
      }
      
      // User wants to downgrade to Free from a paid plan
      toast.info('To downgrade to Free, please cancel your current subscription in the Stripe portal. Your subscription will remain active until the end of your billing period, then you\'ll be automatically moved to Free.');
      return;
    }
    
    // Check if already on the selected plan
    if (currentPlan === plan.name) {
      toast.info(`You are already on the ${plan.name} plan`);
      return;
    }

    setProcessingPayment(true);
    setPaymentPlan(plan);

    try {
      // Create checkout session
      const response = await axios.post(`${API}/payments/checkout/session`, {
        subscription_plan_id: plan.id,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create payment session');
      setProcessingPayment(false);
      setPaymentPlan(null);
    }
  };

  // Check for payment status on component mount (for return from Stripe)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your subscription status.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription has been activated.');
        // Clean up URL and refresh
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => window.location.reload(), 1500);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // If payment is still pending, continue polling
      if (attempts === 0) {
        toast.info('Processing payment...', { duration: pollInterval });
      }
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status. Please contact support if you were charged.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleManagePaymentDetails = async () => {
    try {
      setProcessingPayment(true);
      toast.info('Redirecting to payment portal...');
      
      const response = await axios.post(`${API}/payments/customer-portal`);
      
      if (response.data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = response.data.url;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error) {
      console.error('Customer portal error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to open payment portal';
      toast.error(errorMessage);
      setProcessingPayment(false);
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName.toLowerCase()) {
      case 'basic': return <Star className="w-6 h-6 text-blue-600" />;
      case 'premium': return <Crown className="w-6 h-6 text-purple-600" />;
      case 'professional': return <Shield className="w-6 h-6 text-gold-600" />;
      default: return <Star className="w-6 h-6 text-gray-600" />;
    }
  };

  const getPlanColor = (planName) => {
    switch (planName.toLowerCase()) {
      case 'basic': return 'border-blue-200 hover:border-blue-300';
      case 'premium': return 'border-purple-200 hover:border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50';
      case 'professional': return 'border-yellow-200 hover:border-yellow-300 bg-gradient-to-br from-yellow-50 to-orange-50';
      default: return 'border-gray-200 hover:border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Star className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-bounce" />
          <p className="text-gray-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription Plans</h2>
          <p className="text-gray-600 mt-1">Choose the plan that's right for you</p>
        </div>
        <Button variant="outline" onClick={onBack} data-testid="back-to-dashboard">
          ← Back to Dashboard
        </Button>
      </div>

      {/* Promo Code Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Gift className="w-5 h-5 text-green-600 mr-2" />
            Have a Promo Code?
          </CardTitle>
          <CardDescription>
            Enter your promo code to get free subscription time or discounts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="Enter promo code"
              className="flex-1"
              data-testid="promo-code-input"
            />
            <Button onClick={handleRedeemCode} className="bg-green-600 hover:bg-green-700" data-testid="redeem-code-btn">
              <Gift className="w-4 h-4 mr-2" />
              Redeem
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Manage Payment Details Section */}
      {user?.subscription && user.subscription.plan_name !== 'Free' && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="w-5 h-5 text-blue-600 mr-2" />
              Manage Payment Details & Subscription
            </CardTitle>
            <CardDescription>
              Update your payment method, view invoices, manage your billing information, or cancel your subscription
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              onClick={handleManagePaymentDetails} 
              className="bg-blue-600 hover:bg-blue-700 w-full"
              disabled={processingPayment}
              data-testid="manage-payment-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {processingPayment ? 'Opening Portal...' : 'Manage Payment Details'}
            </Button>
            
            <div className="pt-3 border-t border-blue-200">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Want to downgrade to Free?</strong>
              </p>
              <p className="text-sm text-gray-600 mb-3">
                Click "Manage Payment Details" above, then select "Cancel Subscription" in the Stripe portal. Your subscription will remain active until the end of your billing period, then you'll be moved to the Free plan automatically.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card 
            key={plan.id} 
            className={`relative transition-all duration-200 ${getPlanColor(plan.name)} ${
              plan.name.toLowerCase() === 'premium' ? 'ring-2 ring-purple-200 scale-105' : ''
            } ${
              plan.name.toLowerCase() === 'professional' ? 'opacity-75' : ''
            }`}
          >
            {plan.name.toLowerCase() === 'premium' && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-purple-600 text-white">Most Popular</Badge>
              </div>
            )}
            {plan.name.toLowerCase() === 'professional' && (
              <>
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                  <Badge className="bg-orange-600 text-white shadow-lg">Coming Soon</Badge>
                </div>
                <div className="absolute inset-0 bg-gray-200 bg-opacity-40 rounded-lg z-10"></div>
              </>
            )}
            
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2">
                {getPlanIcon(plan.name)}
              </div>
              <CardTitle className="text-xl">{plan.name}</CardTitle>
              <CardDescription>{plan.description}</CardDescription>
              <div className="text-3xl font-bold text-gray-900 mt-2">
                ${plan.price}
                <span className="text-lg font-normal text-gray-600">/month</span>
              </div>
            </CardHeader>
            
            <CardContent>
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                className={`w-full ${
                  plan.name.toLowerCase() === 'professional' ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed' : 
                  plan.name === 'Free' && user?.subscription?.plan_name !== 'Free' ? 'bg-gray-400 hover:bg-gray-400' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
                onClick={() => handleSubscribe(plan)}
                disabled={
                  processingPayment || 
                  (user?.subscription?.plan_name === plan.name) || 
                  (plan.name.toLowerCase() === 'professional')
                }
                data-testid={`subscribe-${plan.name.toLowerCase()}-btn`}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {plan.name.toLowerCase() === 'professional' ? (
                  'Coming Soon'
                ) : processingPayment && paymentPlan?.id === plan.id ? (
                  'Processing...'
                ) : user?.subscription?.plan_name === plan.name ? (
                  'Current Plan'
                ) : plan.name === 'Free' && user?.subscription?.plan_name !== 'Free' ? (
                  'Cancel to Downgrade'
                ) : (
                  `Choose ${plan.name}`
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SubscriptionPlans;